import {isNotNil} from 'ramda';

import {FacebookSdk} from '../types/FacebookSdk';

const FACEBOOK_SDK_URL = 'https://connect.facebook.net/en_US/sdk.js';

let sdkPromise: Promise<FacebookSdk> | null = null;

const isFacebookSdk = (value: unknown): value is FacebookSdk =>
  typeof value === 'object' &&
  isNotNil(value) &&
  'init' in value &&
  'login' in value &&
  typeof value.init === 'function' &&
  typeof value.login === 'function';

const getLoadedSdk = () => {
  const sdk: unknown = Reflect.get(window, 'FB');
  return isFacebookSdk(sdk) ? sdk : null;
};

/**
 * Loads Meta's JavaScript SDK once per page and resolves with `window.FB`.
 * A failed load is not cached, so the next call retries.
 */
export function loadFacebookSdk() {
  const loadedSdk = getLoadedSdk();
  if (isNotNil(loadedSdk)) {
    return Promise.resolve(loadedSdk);
  }

  sdkPromise ??= new Promise<FacebookSdk>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = FACEBOOK_SDK_URL;
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';
    script.onload = () => {
      const sdk = getLoadedSdk();
      if (isNotNil(sdk)) {
        resolve(sdk);
        return;
      }
      script.remove();
      sdkPromise = null;
      reject(new Error('Facebook SDK loaded without window.FB'));
    };
    script.onerror = () => {
      script.remove();
      sdkPromise = null;
      reject(new Error('Facebook SDK failed to load'));
    };
    document.body.appendChild(script);
  });

  return sdkPromise;
}
